import {
  DocumentDuplicateIcon,
  EyeIcon,
  PencilSquareIcon,
  RectangleStackIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ReferencePreview } from '../../components/studio/ReferencePreview';
import { showConfirmModal, showContentModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import {
  deleteReferenceEntry,
  duplicateReferenceEntry,
  listReferenceDependencies,
  listReferenceEntries
} from '../../data/reference';
import type { ReferenceEntrySummary, ReferenceKind, ReferenceUsage } from '../../data/types';

const KINDS: ReferenceKind[] = ['text', 'file', 'table', 'imageCollection'];

export function ReferenceDataPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [entries, setEntries] = useState<ReferenceEntrySummary[]>([]);
  const [kind, setKind] = useState<string>('');
  const [scope, setScope] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      listReferenceEntries({ kind: kind || undefined, scope: scope || undefined })
        .then((response) => {
          setEntries(response.items ?? []);
          setError(null);
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : t('errors.loadReference'));
        })
        .finally(() => {
          setLoading(false);
        }),
    [kind, scope, t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const duplicate = async (entry: ReferenceEntrySummary) => {
    const newKey = `${entry.key}-copy`;
    try {
      await duplicateReferenceEntry(entry.scope, entry.key, newKey);
      toast({ title: t('reference.duplicated', { key: newKey }), type: 'success' });
      void load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : t('errors.saveReference'), type: 'error' });
    }
  };

  const describeUsage = (usage: ReferenceUsage) => {
    const values = { owner: usage.ownerName || usage.ownerId, token: usage.propertyToken };
    if (usage.reason === 'binding') return t('reference.dependencyBinding', values);
    if (usage.reason === 'driver') return t('reference.dependencyDriver', values);
    return t('reference.dependencyDefault', values);
  };

  const remove = async (entry: ReferenceEntrySummary) => {
    // TA-16: show what breaks before asking, not after the delete fails.
    let usages: ReferenceUsage[] = [];
    try {
      usages = await listReferenceDependencies(entry.scope, entry.key);
    } catch {
      // A failed dependency check must not block the delete; the API re-checks anyway.
    }

    const confirmed = await showConfirmModal({
      title: t('reference.deleteTitle'),
      description:
        usages.length > 0
          ? [
              t('reference.dependenciesTitle', { count: usages.length }),
              ...usages.map(describeUsage),
              t('reference.deleteBlocked')
            ].join('\n')
          : t('reference.deleteDescription', { key: entry.key }),
      confirmLabel: usages.length > 0 ? t('reference.deleteAnyway') : t('reference.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteReferenceEntry(entry.scope, entry.key, usages.length > 0);
      toast({ title: t('reference.deleted'), type: 'success' });
      void load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : t('errors.deleteReference'), type: 'error' });
    }
  };

  const summaryOf = (entry: ReferenceEntrySummary) => {
    if (entry.kind === 'text') return entry.value;
    if (entry.kind === 'imageCollection') return t('reference.itemCount', { count: entry.itemCount });
    if (entry.libraryItemId) return t('reference.inSharePoint');
    return entry.fileName;
  };

  // Legacy precedence: a concrete Office extension beats the broader category. The category
  // is suppressed for table/imageCollection, where it only restates the kind pill next to it.
  const detectedTypeOf = (entry: ReferenceEntrySummary) => {
    if (entry.fileExtension) return entry.fileExtension.toUpperCase();
    if (entry.fileCategory && entry.kind === 'file') {
      return t(`reference.categories.${entry.fileCategory}`);
    }
    return null;
  };

  const iconButton =
    'inline-flex shrink-0 items-center justify-center rounded border border-input bg-surface p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
            {t('reference.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('reference.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/studio/reference/new"
            className="inline-flex items-center rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            {t('studio.referenceEntry.create')}
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4">
        <label className="text-sm">
          <span className="font-medium text-foreground">{t('reference.filterKind')}</span>
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value)}
            className="mt-1 block rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          >
            <option value="">{t('reference.allKinds')}</option>
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {t(`reference.kinds.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="font-medium text-foreground">{t('reference.filterScope')}</span>
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            className="mt-1 block rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          >
            <option value="">{t('reference.allScopes')}</option>
            <option value="global">{t('reference.global')}</option>
          </select>
        </label>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error && entries.length === 0 ? (
        <section className="mt-6 rounded-lg border border-border bg-surface p-6">
          <div className="grid size-10 place-items-center rounded bg-accent text-accent-foreground">
            <RectangleStackIcon className="size-5" aria-hidden="true" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-foreground">{t('reference.emptyTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('reference.emptyBody')}</p>
        </section>
      ) : null}

      {!loading && !error && entries.length > 0 ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('reference.columns.key')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('reference.columns.kind')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('reference.columns.scope')}</th>
                <th scope="col" className="px-4 py-2.5 text-left font-semibold text-foreground">{t('reference.columns.value')}</th>
                <th scope="col" className="w-px whitespace-nowrap px-4 py-2.5 text-right font-semibold text-foreground">{t('reference.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((entry) => (
                <tr key={`${entry.scope}/${entry.key}`} className="hover:bg-muted">
                  <td className="px-4 py-3">
                    <span className="font-mono text-foreground">{entry.key}</span>
                    <p className="mt-0.5 text-muted-foreground">{entry.title}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      {t(`reference.kinds.${entry.kind}`)}
                    </span>
                    {detectedTypeOf(entry) ? (
                      <span
                        title={t('reference.detectedType')}
                        className="ml-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground"
                      >
                        {detectedTypeOf(entry)}
                      </span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {entry.scope === 'global' ? t('reference.global') : t('reference.templateScoped')}
                  </td>
                  <td className="max-w-md truncate px-4 py-3 text-muted-foreground">{summaryOf(entry)}</td>
                  <td className="w-px whitespace-nowrap px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          showContentModal({
                            title: t('reference.previewTitle', { key: entry.key }),
                            description: entry.description || undefined,
                            size: 'wide',
                            body: <ReferencePreview scope={entry.scope} entryKey={entry.key} />
                          });
                        }}
                        aria-label={t('reference.preview')}
                        className={iconButton}
                      >
                        <EyeIcon className="size-5 shrink-0" aria-hidden="true" />
                      </button>
                      <Link
                        to={`/studio/reference/${encodeURIComponent(entry.key)}?scope=${encodeURIComponent(entry.scope)}`}
                        aria-label={t('studio.referenceEntry.titleEdit')}
                        className={iconButton}
                      >
                        <PencilSquareIcon className="size-5 shrink-0" aria-hidden="true" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => void duplicate(entry)}
                        aria-label={t('reference.duplicate')}
                        className={iconButton}
                      >
                        <DocumentDuplicateIcon className="size-5 shrink-0" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(entry)}
                        aria-label={t('reference.delete')}
                        className={iconButton}
                      >
                        <TrashIcon className="size-5 shrink-0" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
